import React, { useState, useEffect, useRef } from 'react';
import { getProfile, updateProfile, updateProfileWithPicture } from '../api';
import { useNavigate } from 'react-router-dom';

function ProfilePage() {
  const [profile, setProfile] = useState({
    displayName: '',
    bio: '',
    location: '',
    jobTitle: ''
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      setError('');
      const profileData = await getProfile();
      setProfile({
        displayName: profileData.displayName || '',
        bio: profileData.bio || '',
        location: profileData.location || '',
        jobTitle: profileData.jobTitle || ''
      });
      
      // Set profile picture URL if available
      if (profileData.profilePictureUrl) {
        setProfilePictureUrl(profileData.profilePictureUrl);
      }
    } catch (err) {
      setError('Failed to load profile data. Please try again.');
      console.error('Error loading profile:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        setError('Please select an image file (JPEG, PNG, etc.)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file size should be less than 5MB');
        return;
      }
      
      setProfilePicture(file);
      // Create a preview URL
      setProfilePictureUrl(URL.createObjectURL(file));
    }
  };
  
  const handleFileButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      setError('');
      setSuccessMessage('');
      
      // If a new profile picture was selected, use the method with picture upload
      if (profilePicture) {
        await updateProfileWithPicture(profile, profilePicture);
      } else {
        await updateProfile(profile);
      }
      
      setSuccessMessage('Profile updated successfully!');
      
      // Reset the file input and selected file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setProfilePicture(null);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
      console.error('Error updating profile:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full p-8">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="card bg-base-200 shadow-xl my-4">
        <div className="card-body">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Your Profile</h2>
            <button 
              className="btn btn-outline btn-sm"
              onClick={() => navigate('/')}
            >
              Back to Home
            </button>
          </div>
          
          {error && (
            <div className="alert alert-error mb-4">
              <span>{error}</span>
            </div>
          )}
          
          {successMessage && (
            <div className="alert alert-success mb-4">
              <span>{successMessage}</span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="bg-base-300 p-6 rounded-lg">
            {/* Profile Picture Upload */}
            <div className="form-control mb-6 flex items-center">
              <div className="avatar mb-3">
                <div className="w-24 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2">
                  {profilePictureUrl ? (
                    <img src={profilePictureUrl} alt="Profile" />
                  ) : (
                    <div className="bg-neutral-focus text-neutral-content rounded-full w-24 h-24 flex items-center justify-center">
                      <span className="text-3xl">{profile.displayName ? profile.displayName[0].toUpperCase() : '?'}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                ref={fileInputRef}
              />
              
              <button 
                type="button" 
                className="btn btn-outline btn-primary"
                onClick={handleFileButtonClick}
              >
                Change Profile Picture
              </button>
            </div>
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">Display Name</span>
              </label>
              <input 
                type="text" 
                name="displayName" 
                value={profile.displayName}
                onChange={handleChange}
                className="input input-bordered" 
                placeholder="Your display name"
              />
            </div>
            
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">Job Title</span>
              </label>
              <input 
                type="text" 
                name="jobTitle" 
                value={profile.jobTitle}
                onChange={handleChange}
                className="input input-bordered" 
                placeholder="Your job title"
              />
            </div>
            
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">Location</span>
              </label>
              <input 
                type="text" 
                name="location" 
                value={profile.location}
                onChange={handleChange}
                className="input input-bordered" 
                placeholder="Your location"
              />
            </div>
            
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">Bio</span>
              </label>
              <textarea 
                name="bio" 
                value={profile.bio}
                onChange={handleChange}
                className="textarea textarea-bordered h-24" 
                placeholder="Tell us about yourself"
              />
            </div>
            
            <div className="form-control mt-6">
              <button 
                type="submit" 
                className={`btn btn-primary ${isSaving ? 'loading' : ''}`} 
                disabled={isSaving}
              >
                Save Profile
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
